import { Plus, Trash2 } from "lucide-react";

import {
  WEEKDAYS,
  WEEKDAY_LABELS,
  emptyOpenHours,
  type OpenHours,
} from "../../../Services/Admin/Venues";
import "./OpenHoursEditor.css";

/**
 * Öppettider, en dag i taget.
 *
 * Bruten ur VenueEditor för att kunna användas på båda ställen där
 * öppettider sätts. Den andra — formuläret för nya venues på kartan — lät
 * tidigare admin skriva JSON för hand, vilket var både lätt att skriva fel
 * och omöjligt att skumma.
 *
 * Komponenten är kontrollerad och äger ingenting: den visar det den får
 * och rapporterar varje ändring uppåt. Att avgöra om tiderna duger hör
 * hemma hos den som sparar, inte här.
 */

type Props = {
  value: OpenHours;
  onChange: (next: OpenHours) => void;

  /** Visas när inget pass är ifyllt. Öppettider är obligatoriska. */
  showRequiredWarning?: boolean;
};

/** Ett pass ska kunna läggas till utan att först välja tider. */
const DEFAULT_INTERVAL = { open: "18:00", close: "01:00" };

export default function OpenHoursEditor({
  value,
  onChange,
  showRequiredWarning = true,
}: Props) {
  const hasAnyHours = WEEKDAYS.some((day) => (value[day] ?? []).length > 0);

  const addInterval = (day: string) => {
    onChange({ ...value, [day]: [...(value[day] ?? []), { ...DEFAULT_INTERVAL }] });
  };

  const removeInterval = (day: string, index: number) => {
    onChange({ ...value, [day]: (value[day] ?? []).filter((_, i) => i !== index) });
  };

  const setTime = (day: string, index: number, field: "open" | "close", next: string) => {
    onChange({
      ...value,
      [day]: (value[day] ?? []).map((interval, i) =>
        i === index ? { ...interval, [field]: next || null } : interval
      ),
    });
  };

  const copyMondayToWeekdays = () => {
    const monday = value.mon ?? [];
    const next = { ...value };
    for (const day of ["tue", "wed", "thu", "fri"]) {
      next[day] = monday.map((interval) => ({ ...interval }));
    }
    onChange(next);
  };

  return (
    <div className="hours-editor">
      <div className="hours-editor-head">
        <span className="admin-label">Opening hours (required)</span>
        <div className="hours-editor-head-actions">
          <button type="button" className="admin-btn--sm" onClick={copyMondayToWeekdays}>
            Copy Mon to Tue–Fri
          </button>
          <button
            type="button"
            className="admin-btn--sm"
            onClick={() => onChange(emptyOpenHours())}
          >
            Clear all
          </button>
        </div>
      </div>

      {WEEKDAYS.map((day) => (
        <div className="hours-editor-day" key={day}>
          <span className="hours-editor-day-name">{WEEKDAY_LABELS[day]}</span>

          <div className="hours-editor-intervals">
            {(value[day] ?? []).length === 0 && (
              <span className="hours-editor-closed">Closed</span>
            )}

            {(value[day] ?? []).map((interval, index) => (
              <div className="hours-editor-interval" key={index}>
                <input
                  type="time"
                  className="hours-editor-time"
                  value={interval.open}
                  onChange={(e) => setTime(day, index, "open", e.target.value)}
                />
                <span>–</span>
                <input
                  type="time"
                  className="hours-editor-time"
                  value={interval.close ?? ""}
                  onChange={(e) => setTime(day, index, "close", e.target.value)}
                  title="Leave empty when the closing time is unknown"
                />
                <button
                  type="button"
                  className="hours-editor-icon"
                  onClick={() => removeInterval(day, index)}
                  aria-label={`Remove interval for ${WEEKDAY_LABELS[day]}`}
                >
                  <Trash2 size={14} strokeWidth={2.2} />
                </button>
              </div>
            ))}

            <button
              type="button"
              className="hours-editor-icon"
              onClick={() => addInterval(day)}
              aria-label={`Add interval for ${WEEKDAY_LABELS[day]}`}
            >
              <Plus size={14} strokeWidth={2.2} />
            </button>
          </div>
        </div>
      ))}

      {showRequiredWarning && !hasAnyHours && (
        <p className="hours-editor-warning">
          No hours set. The sensor will not recognise anything at this venue until at
          least one interval is filled in.
        </p>
      )}
    </div>
  );
}
